import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { Input, Loader, Modal, SelectPicker, Uploader } from "rsuite";
import { Banner } from "../../../models/sales/banner";
import { Form, Button } from "rsuite";
import { errorToast, successToast } from "../../../utils/helper-ui";
import { BsCamera, BsImage } from "react-icons/bs";
import { asyncLocalStorage } from "../../../utils/storage";
import { Company } from "../../../models/company/company";
import { getCompanies } from "../../../services/company/CompanyApi";

import { Editor } from "@tinymce/tinymce-react";
import { useRealm } from "@mdxeditor/editor";
import { FileModel } from "../../../models/file/file";
import { TrashIcon } from "@heroicons/react/24/outline";
interface BannerFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Banner) => void;
  banner?: Banner;
}

const BannerForm: FC<BannerFormProps> = ({
  open,
  onClose,
  onSubmit,
  banner,
}) => {
  const editorRef = useRef<React.MutableRefObject<null>>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | null | undefined
  >(null);
  const [editorContent, setEditorContent] = useState("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [files, setFiles] = useState<FileModel[]>([]);
  const [data, setData] = useState<Banner>({
    id: "",
    title: "",
    description: "",
    url: "",
  });
  const [uploading, setUploading] = useState(false);
  const [token, setToken] = useState("");

  useEffect(() => {
    asyncLocalStorage.getItem("token").then((token) => {
      setToken(token);
    });
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ font: [] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ script: "sub" }, { script: "super" }],
          ["blockquote", "code-block"],
          [{ list: "ordered" }, { list: "bullet" }],

          [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
          [{ direction: "rtl" }],
          [{ size: ["small", false, "large", "huge"] }],
          ["link", "image", "video"],
          ["clean"],
        ],

        history: {
          delay: 500,
          maxStack: 100,
          userOnly: true,
        },
      },
    }),
    []
  );

  const getAllCompanies = async (search?: string) => {
    try {
      const res = await getCompanies({ page: 1, size: 10, search });
      const data = await res.json();
      setCompanies(data.data.items);
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditorChange = (content: any, editor: any) => {
    setEditorContent(content);
  };

  useEffect(() => {
    setData(
      banner || {
        id: "",
        title: "",
        description: "",
        url: "",
      }
    );

    if (banner) {
      setFiles(banner?.images ?? []);
    }
  }, [banner]);
  return (
    <Modal size={"lg"} open={open} onClose={onClose}>
      <Modal.Header>
        <Modal.Title>Banner Form</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form className="w-full" layout="horizontal" fluid>
          <div className="form-group">
            <label>Title</label>
            <Input
              name="name"
              placeholder="e.g. Nike"
              value={data.title}
              onChange={(value) => setData({ ...data, title: value })}
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <div className="input min-h-[300px]">
              <Editor
                apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                // onInit={(_evt, editor) => (editorRef!.current = editor)}
                initialValue={data.description ?? ""}
                init={{
                  height: 500,
                  menubar: false,
                  plugins: [
                    "advlist",
                    "autolink",
                    "lists",
                    "link",
                    "image",
                    "charmap",
                    "preview",
                    "anchor",
                    "searchreplace",
                    "visualblocks",
                    "code",
                    "fullscreen",
                    "insertdatetime",
                    "media",
                    "table",
                    "code",
                    "help",
                    "wordcount",
                  ],
                  toolbar:
                    "undo redo | blocks | " +
                    "bold italic forecolor | alignleft aligncenter " +
                    "alignright alignjustify | bullist numlist outdent indent | " +
                    "removeformat | help",
                  content_style:
                    "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                }}
                onEditorChange={handleEditorChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Logo</label>
            <div className="input">
              <Uploader
                fileListVisible={false}
                listType="picture"
                accept="image/*"
                action={process.env.REACT_APP_API_URL + "/v1/file/upload"}
                onUpload={(file) => {
                  setUploading(true);
                }}
                className="w-full"
                headers={{
                  Authorization: `Bearer ${token}`,
                }}
                onSuccess={(response, file) => {
                  setUploading(false);
                  successToast("File uploaded successfully");
                  setFiles((val) => {
                    return [...val, response.file];
                  });
                }}
                onError={() => {
                  setUploading(false);
                  errorToast("Upload failed");
                }}
              >
                <div
                  style={{
                    height: 200,
                    width: "100%",
                    padding: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {uploading && <Loader backdrop center />}
                  <span>Click or Drag files to this area to upload</span>
                </div>
              </Uploader>
              <div className="flex flex-row gap-2 w-full">
                {files.map((file) => (
                  <div style={{ width: 100, height: 100 }} className="relative">
                    <img
                      src={file.url}
                      style={{ width: 100, height: 100 }}
                      alt={file.path}
                    />
                    <TrashIcon
                      className="absolute top-1 right-1 cursor-pointer text-red-400 hover:text-red-600 w-4"
                      onClick={() => {
                        setFiles((val) => {
                          return val.filter((f) => f.id !== file.id);
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="form-group">
            <label>URL</label>
            <Input
              name="url"
              placeholder="e.g. https://www.nike.com"
              value={data.url}
              onChange={(e) => setData({ ...data, url: e })}
            />
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="pt-4">
          <Button
            appearance="primary"
            onClick={() => {
              data.description = editorContent;
              data.images = files;
              onSubmit(data!);
              setData({
                id: "",
                title: "",
                description: "",
                url: "",
              });
            }}
          >
            Submit
          </Button>
          <Button appearance="link" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default BannerForm;
